// Maps a resolved route to the GA4 page_viewed dimensions. page_type is the
// path's section (`pageSection` in `page-sections.ts`, the same table its
// title subject comes from), so it is stable across the _authed and _public
// route trees, which share URL space. page_id comes from the leaf match's
// params, under the param the section names, so it is the clean route param
// rather than a string-parsed segment.
//
// A route whose URL does not carry the entity's id (the member profile is
// addressed by handle) returns `{ pageId }` from its loader. page_id is the
// pseudonymous uuid, never a name-derived handle, so profile rows stay
// joinable however the member's URL is spelled, and a profile whose read
// failed reports no page_id rather than the handle.
//
// page_title is the same string the navigation writes to `document.title`,
// so the explicit param and gtag's own auto-captured title cannot disagree.
// A loader's `pageTitle` is the entity's title subject; see `page-sections.ts`
// for which routes may supply one.

import { pageSection, type Ga4PageType } from '@/lib/page-sections';
import { pageSubject } from '@/lib/page-title';
import { composeTitle } from '@/lib/seo-text';

export interface PageMetaLoaderData {
  readonly pageId?: string;
  readonly pageTitle?: string;
}

// The `staleReloadMode` of a route whose loader titles the tab from copy an
// editor can change in this session: the resource and learning track pages.
// A revisited route's loader data is stale, and by default the router resolves
// the navigation with the previous visit's data and reloads it behind; the tab
// is titled once per navigation from the data it resolved with
// (`subscribePageViews`), so a title an editor changed since that visit would
// stay off the tab until the visit after. Blocking makes the navigation
// resolve with the reloaded data. Those loaders read the query cache and fetch
// only when it holds nothing current (`loadCurrentForRoute`), so a blocking
// reload is a cache read unless the entity was written since. Every other
// route keeps the router's background reload, so a revisit renders from cache
// at once. The static pages need neither: their title is read from the
// page-seo cache, not from loader data.
export const EDITABLE_TITLE_RELOAD = 'blocking' as const;

export interface PageMeta {
  readonly page_type: Ga4PageType;
  readonly page_id: string | null;
  readonly page_title: string;
}

export function resolvePageMeta(
  pathname: string,
  params: Record<string, string>,
  loaderData?: PageMetaLoaderData,
): PageMeta {
  const page_title = composeTitle(loaderData?.pageTitle ?? pageSubject(pathname));
  const section = pageSection(pathname);
  if (section === null || section.pageType === undefined) {
    return { page_type: 'other', page_id: null, page_title };
  }
  const urlId = section.pageIdParam === null ? undefined : params[section.pageIdParam];
  return {
    page_type: section.pageType,
    page_id: loaderData?.pageId ?? urlId ?? null,
    page_title,
  };
}
